import { NewWorkEntry, WorkDay, WorkEntry } from '../../types'
import { apiSlice } from '../api/apiSlice'
import { changeEmptyStringsToNull, changeNullsToEmptyString } from '../../utils/falsyValuesConverter'
import { WorkHoursMonthlySummaryDTO } from '../../types/utils/WorklogTypes'

type GetWorklogArgs = {
  employeeId: string | undefined
  date: string
  all?: boolean
}

type AddEntryArgs = {
  employeeId: string
  entry: Partial<NewWorkEntry>
}

type EditEntryArgs = {
  employeeId: string
  entryId: string
  entry: Partial<NewWorkEntry>
}

type DeleteEntryArgs = {
  employeeId: string
  entry: Partial<WorkEntry>
}

type DeleteEntriesArgs = {
  employeeId: string
  workHoursIds: string[]
}

type ImportDataArgs = {
  data: Uint8Array
  employeeId: string
}

type TransferProjectArgs = {
  employeeId?: string
  transfer: {
    sourceProjectId: string
    destinationProjectId: string
    startDate: string
    endDate: string
  }
}

type TransferDatesArgs = {
  employeeId?: string
  transfer: {
    oldStartDate: string
    oldEndDate: string
    newStartDate: string
  }
}

type WorklogResponse = Record<string, WorkEntry[]>

const uint8ArrayToBase64 = (uint8Array: Uint8Array) => {
  let binary = ''
  const len = uint8Array.byteLength
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(uint8Array[i])
  }
  return window.btoa(binary)
}

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getUsersWorklog: builder.query<WorkDay[], GetWorklogArgs>({
      query: ({ employeeId, date, all }) => ({
        url: `/${employeeId}/worklog/work-hours?date=${date}${all !== undefined ? `&all=${all}` : ''}`,
        method: 'GET',
      }),
      transformResponse: (response: WorklogResponse) => {
        let newResponse: WorkDay[] = []
        if (response === undefined) return newResponse
        for (const day in response) {
          let entries: WorkEntry[] = response[day].map((e: WorkEntry) => changeNullsToEmptyString(e))
          let workDay: WorkDay = {
            date: day,
            entries: entries,
          }
          newResponse.push(workDay)
        }
        return newResponse
      },
      providesTags: () => [{ type: 'Worklog', id: 'LIST' }],
    }),
    addUsersWorklogEntry: builder.mutation<WorkEntry, AddEntryArgs>({
      query: ({ employeeId, entry }: AddEntryArgs) => ({
        url: `${employeeId}/worklog/work-hours`,
        method: 'POST',
        body: changeEmptyStringsToNull(entry),
      }),
      invalidatesTags: () => [
        { type: 'Worklog', id: 'LIST' },
        { type: 'YearlySummary', id: 'LIST' },
        { type: 'Vacation', id: 'LIST' },
        { type: 'Vacation', id: 'UPCOMING_LIST' },
        { type: 'VacationAcknowledgement' },
      ],
    }),
    editUsersWorklogEntry: builder.mutation<WorkEntry, EditEntryArgs>({
      query: ({ employeeId, entryId, entry }: EditEntryArgs) => ({
        url: `/${employeeId}/worklog/work-hours/${entryId}`,
        method: 'PUT',
        body: changeEmptyStringsToNull(entry),
      }),
      invalidatesTags: () => [
        { type: 'Worklog', id: 'LIST' },
        { type: 'YearlySummary', id: 'LIST' },
        { type: 'Vacation', id: 'LIST' },
        { type: 'Vacation', id: 'UPCOMING_LIST' },
        { type: 'VacationAcknowledgement' },
      ],
    }),
    deleteUserWorklogEntry: builder.mutation<WorkEntry, DeleteEntryArgs>({
      query: ({ employeeId, entry }: DeleteEntryArgs) => ({
        url: `/${employeeId}/worklog/work-hours/${entry.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [
        { type: 'Worklog', id: 'LIST' },
        { type: 'YearlySummary', id: 'LIST' },
        { type: 'Vacation', id: 'LIST' },
        { type: 'Vacation', id: 'UPCOMING_LIST' },
        { type: 'VacationAcknowledgement' },
      ],
    }),
    deleteUserWorklogEntries: builder.mutation<WorkEntry, DeleteEntriesArgs>({
      query: ({ employeeId, workHoursIds }: DeleteEntriesArgs) => ({
        url: `/${employeeId}/worklog/work-hours/multiple`,
        method: 'DELETE',
        body: workHoursIds,
      }),
      invalidatesTags: () => [
        { type: 'Worklog', id: 'LIST' },
        { type: 'YearlySummary', id: 'LIST' },
        { type: 'Vacation', id: 'LIST' },
        { type: 'Vacation', id: 'UPCOMING_LIST' },
        { type: 'VacationAcknowledgement' },
      ],
    }),
    importData: builder.mutation<void, ImportDataArgs>({
      query: ({ data, employeeId }: ImportDataArgs) => {
        const base64Data = uint8ArrayToBase64(data)
        return {
          url: `/import`,
          method: 'POST',
          body: { data: base64Data, employeeId },
        }
      },
      invalidatesTags: () => [
        { type: 'Worklog', id: 'LIST' },
        { type: 'Cost', id: 'LIST' },
        { type: 'Vacation', id: 'LIST' },
        { type: 'Vacation', id: 'UPCOMING_LIST' },
        { type: 'VacationAcknowledgement' },
      ],
    }),
    transferProject: builder.mutation<WorkDay[], TransferProjectArgs>({
      query: ({ employeeId, transfer }) => ({
        url: `/${employeeId}/worklog/work-hours/transfer/project`,
        method: 'POST',
        body: transfer,
      }),
      transformResponse: (response: WorklogResponse) => {
        let newResponse: WorkDay[] = []
        if (response === undefined) return newResponse
        for (const day in response) {
          let entries: WorkEntry[] = response[day].map((e: WorkEntry) => changeNullsToEmptyString(e))
          let workDay: WorkDay = {
            date: day,
            entries: entries,
          }
          newResponse.push(workDay)
        }
        return newResponse
      },
      invalidatesTags: () => [
        { type: 'Worklog', id: 'LIST' },
        { type: 'YearlySummary', id: 'LIST' },
        { type: 'Vacation', id: 'LIST' },
        { type: 'Vacation', id: 'UPCOMING_LIST' },
        { type: 'VacationAcknowledgement' },
      ],
    }),
    transferDates: builder.mutation<WorkDay[], TransferDatesArgs>({
      query: ({ employeeId, transfer }) => ({
        url: `/${employeeId}/worklog/work-hours/transfer/date`,
        method: 'POST',
        body: transfer,
      }),
      transformResponse: (response: WorklogResponse) => {
        let newResponse: WorkDay[] = []
        if (response === undefined) return newResponse
        for (const day in response) {
          let entries: WorkEntry[] = response[day].map((e: WorkEntry) => changeNullsToEmptyString(e))
          let workDay: WorkDay = {
            date: day,
            entries: entries,
          }
          newResponse.push(workDay)
        }
        return newResponse
      },
      invalidatesTags: () => [
        { type: 'Worklog', id: 'LIST' },
        { type: 'YearlySummary', id: 'LIST' },
        { type: 'Vacation', id: 'LIST' },
        { type: 'Vacation', id: 'UPCOMING_LIST' },
        { type: 'VacationAcknowledgement' },
      ],
    }),
    getWorkHoursSummary: builder.query<WorkHoursMonthlySummaryDTO, { date: string }>({
      query: ({ date }) => ({
        url: `/worklog/work-hours/summary?date=${date}`,
        method: 'GET',
      }),
      providesTags: () => [{ type: 'Worklog', id: 'LIST' }],
    }),
  }),
})

export const {
  useGetUsersWorklogQuery,
  useAddUsersWorklogEntryMutation,
  useEditUsersWorklogEntryMutation,
  useDeleteUserWorklogEntryMutation,
  useDeleteUserWorklogEntriesMutation,
  useImportDataMutation,
  useTransferProjectMutation,
  useTransferDatesMutation,
  useGetWorkHoursSummaryQuery,
} = extendedApiSlice
